import { Button } from '@heidi/design-system/button';
import { ThumbsDownFilled, ThumbsUpFilled } from '@heidi/design-system/feedback-thumbs';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { Textarea } from '@heidi/design-system/textarea';
import { toast } from '@heidi/design-system/toast';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyP2Bold } from '@heidi/design-system/typography';
import { useSetAtom } from 'jotai';
import { ThumbsDown, ThumbsUp, type LucideIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import { cn, useOverlayTriggerTooltip } from '@heidi/utils';
import type { VoiceFeedbackRating } from '../../../contracts/voice-feedback.types';
import { useSubmitVoiceFeedback } from '../../../data/voice/useSubmitVoiceFeedback';
import { voiceFeedbackMessages } from './voiceFeedback.messages';
import { voiceFeedbackMarkSubmittedAtom } from './voiceFeedbackAtoms';

export interface VoiceFeedbackThumbsProps {
  className?: string;
  /** Compact icon row for dock/workspace headers. */
  size?: 'sm' | 'md';
  onSubmitted?: () => void;
}

const getFeedbackCopy = (
  intl: IntlShape,
  rating: VoiceFeedbackRating
): { label: string; title: string; placeholder: string } => {
  if (rating === 'thumb_up') {
    return {
      label: intl.formatMessage(voiceFeedbackMessages.positiveTooltip),
      title: intl.formatMessage(voiceFeedbackMessages.thumbUpTitle),
      placeholder: intl.formatMessage(voiceFeedbackMessages.thumbUpPlaceholder),
    };
  }
  return {
    label: intl.formatMessage(voiceFeedbackMessages.negativeTooltip),
    title: intl.formatMessage(voiceFeedbackMessages.thumbDownTitle),
    placeholder: intl.formatMessage(voiceFeedbackMessages.thumbDownPlaceholder),
  };
};

/** Projects-style thumbs for Talk-to-Heidi (POST /voice/feedback). */
export function VoiceFeedbackThumbs({
  className,
  size = 'md',
  onSubmitted,
}: VoiceFeedbackThumbsProps) {
  const intl = useIntl();
  const submitFeedback = useSubmitVoiceFeedback();
  const markSubmitted = useSetAtom(voiceFeedbackMarkSubmittedAtom);
  const [selected, setSelected] = useState<VoiceFeedbackRating | null>(null);
  const [openFor, setOpenFor] = useState<VoiceFeedbackRating | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const handleThumbClick = (rating: VoiceFeedbackRating) => {
    if (openFor === rating) {
      setOpenFor(null);
      return;
    }
    setComment('');
    setOpenFor(rating);
  };

  const handleSubmit = async (rating: VoiceFeedbackRating) => {
    if (submittingRef.current) return;
    const previous = selected;
    const text = comment.trim();
    submittingRef.current = true;
    setSelected(rating);
    setOpenFor(null);
    setIsSubmitting(true);
    try {
      await submitFeedback.mutateAsync({
        rating,
        user_feedback: text || undefined,
      });
      markSubmitted();
      onSubmitted?.();
      toast({
        tone: 'success',
        title: intl.formatMessage(voiceFeedbackMessages.thanksForFeedback),
      });
    } catch {
      setSelected(previous);
      toast({
        tone: 'destructive',
        title: intl.formatMessage(voiceFeedbackMessages.couldntSubmitFeedback),
        description: intl.formatMessage(voiceFeedbackMessages.pleaseTryAgain),
      });
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={cn('flex items-center gap-0.5', className)}
      data-testid="voice-feedback-thumbs"
      aria-label={intl.formatMessage(voiceFeedbackMessages.giveFeedback)}
    >
      <ThumbFeedback
        rating="thumb_up"
        icon={ThumbsUp}
        filledIcon={ThumbsUpFilled}
        size={size}
        isSelected={selected === 'thumb_up'}
        isOpen={openFor === 'thumb_up'}
        isSubmitting={isSubmitting}
        comment={comment}
        onToggle={() => handleThumbClick('thumb_up')}
        onClose={() => setOpenFor(null)}
        onCommentChange={setComment}
        onSubmit={() => void handleSubmit('thumb_up')}
      />
      <ThumbFeedback
        rating="thumb_down"
        icon={ThumbsDown}
        filledIcon={ThumbsDownFilled}
        size={size}
        isSelected={selected === 'thumb_down'}
        isOpen={openFor === 'thumb_down'}
        isSubmitting={isSubmitting}
        comment={comment}
        onToggle={() => handleThumbClick('thumb_down')}
        onClose={() => setOpenFor(null)}
        onCommentChange={setComment}
        onSubmit={() => void handleSubmit('thumb_down')}
      />
    </div>
  );
}

interface ThumbFeedbackProps {
  rating: VoiceFeedbackRating;
  icon: typeof ThumbsUp;
  filledIcon: LucideIcon;
  size: 'sm' | 'md';
  /** The submitted rating; drives the fill, so an open popover alone leaves it outlined. */
  isSelected: boolean;
  isOpen: boolean;
  isSubmitting: boolean;
  comment: string;
  onToggle: () => void;
  onClose: () => void;
  onCommentChange: (value: string) => void;
  onSubmit: () => void;
}

const ThumbFeedback = ({
  rating,
  icon: Icon,
  filledIcon: FilledIcon,
  size,
  isSelected,
  isOpen,
  isSubmitting,
  comment,
  onToggle,
  onClose,
  onCommentChange,
  onSubmit,
}: ThumbFeedbackProps) => {
  const intl = useIntl();
  const { label, title, placeholder } = getFeedbackCopy(intl, rating);
  const { tooltipProps, contentProps } = useOverlayTriggerTooltip();
  const ThumbIcon = isSelected ? FilledIcon : Icon;
  return (
    <Popover
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Tooltip {...tooltipProps}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size={size === 'sm' ? 'sm' : 'md'}
              iconOnly
              onClick={onToggle}
              aria-label={label}
              aria-pressed={isSelected}
              className="text-text-secondary"
              data-testid={`voice-feedback-${rating}`}
            >
              <ThumbIcon aria-hidden="true" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
      <PopoverContent {...contentProps} align="end" side="top" className="w-72 p-3">
        <div className="flex flex-col gap-2">
          <TypographyP2Bold className="text-sm text-text-primary">
            {title}
          </TypographyP2Bold>
          <Textarea
            autoFocus
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            placeholder={placeholder}
            className="h-20 resize-none"
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {intl.formatMessage(voiceFeedbackMessages.cancel)}
            </Button>
            <Button type="button" size="xs" onClick={onSubmit} disabled={isSubmitting}>
              {isSubmitting
                ? intl.formatMessage(voiceFeedbackMessages.submitting)
                : intl.formatMessage(voiceFeedbackMessages.submit)}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
